// University Settings Configuration

import {
  MessageSquare,
  FileText,
  Settings,
  GraduationCap,
  Building2,
  KeyRound,
  Megaphone,
} from "lucide-react";

// API imports
import {
  getLeadDropPopup,
  getLeadDropPopupByVariant,
  createLeadDropPopup,
  getAllLeadDropPopups,
} from "@/api/admin/leadDropPopup";
import {
  getLeadDropForm,
  getLeadDropFormByVariant,
  createLeadDropForm,
  getAllLeadDropForms,
} from "@/api/admin/leadDropForm";
import {
  getCourse,
  getCoursesByVariant,
} from "@/api/admin/course";
import {
  getHigherQualification,
  getHigherQualificationsByUniversitySettingsId,
} from "@/api/admin/higherQualification";
import { getUniversityById } from "@/api/admin/university";
import { getAllUniversitySettings } from "@/api/admin/universitySettings";

// Section types
import {
  UNIVERSITY_SETTINGS_SECTION_TYPES,
  UNIVERSITY_ID,
} from "../sectionTypes";

// Types
import type { UniversitySettingKey, UniversitySettingConfig, VariantConfig } from "./types";

// Variant configs
import {
  LEAD_DROP_POPUP_VARIANTS_CONFIG,
  LEAD_DROP_FORM_VARIANTS_CONFIG,
  COURSES_VARIANTS_CONFIG,
  HIGHER_QUALIFICATION_VARIANTS_CONFIG,
  UNIVERSITY_VARIANTS_CONFIG,
} from "./variants";

// ---------------------------------------------------------------------------
// Unified Settings Configuration
// ---------------------------------------------------------------------------

export const UNIVERSITY_SETTINGS_CONFIG: Record<UniversitySettingKey, UniversitySettingConfig> = {
  popup: {
    key: "popup",
    label: "Lead Drop Popup",
    labelPlural: "Lead Drop Popups",
    description: "Create and manage popup notifications for lead capture.",
    icon: MessageSquare,
    iconColor: "text-purple-600",
    iconBgColor: "bg-purple-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_POPUP,
    variantsConfig: LEAD_DROP_POPUP_VARIANTS_CONFIG,
    api: {
      getAll: getAllLeadDropPopups,
      getByVariant: getLeadDropPopupByVariant,
      getActive: getLeadDropPopup,
      create: createLeadDropPopup,
    },
    ui: {
      showCreateButton: true,
      isDirectForm: false,
      emptyMessage: "No popups created yet",
      sidebarTitle: "Popup Settings",
    },
  },
  form: {
    key: "form",
    label: "Lead Drop Form",
    labelPlural: "Lead Drop Forms",
    description: "Design and manage lead capture forms.",
    icon: FileText,
    iconColor: "text-blue-600",
    iconBgColor: "bg-blue-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_FORM,
    variantsConfig: LEAD_DROP_FORM_VARIANTS_CONFIG,
    api: {
      getAll: getAllLeadDropForms,
      getByVariant: getLeadDropFormByVariant,
      getActive: getLeadDropForm,
      create: createLeadDropForm,
    },
    ui: {
      showCreateButton: true,
      isDirectForm: false,
      emptyMessage: "No forms created yet",
      sidebarTitle: "Form Settings",
    },
  },
  courses: {
    key: "courses",
    label: "Course",
    labelPlural: "Courses",
    description: "Manage course programs.",
    icon: Settings,
    iconColor: "text-green-600",
    iconBgColor: "bg-green-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.COURSES,
    variantsConfig: COURSES_VARIANTS_CONFIG,
    api: {
      getAll: getAllUniversitySettings,
      getByVariant: getCoursesByVariant,
      getActive: getCourse,
    },
    ui: {
      showCreateButton: true,
      isDirectForm: false,
      emptyMessage: "No courses created yet",
      sidebarTitle: "Course Settings",
    },
  },
  higher_qualification: {
    key: "higher_qualification",
    label: "Higher Qualification",
    labelPlural: "Higher Qualifications",
    description: "Manage higher qualification programs.",
    icon: GraduationCap,
    iconColor: "text-orange-600",
    iconBgColor: "bg-orange-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.HIGHER_QUALIFICATION,
    variantsConfig: HIGHER_QUALIFICATION_VARIANTS_CONFIG,
    api: {
      getAll: getAllUniversitySettings,
      getActive: getHigherQualification,
      fetchByUniversitySettingsId: getHigherQualificationsByUniversitySettingsId,
    },
    ui: {
      showCreateButton: true,
      isDirectForm: false,
      emptyMessage: "No higher qualifications created yet",
      sidebarTitle: "Higher Qualification Settings",
    },
  },
  university_details: {
    key: "university_details",
    label: "University Details",
    labelPlural: "University Details",
    description: "Manage university information.",
    icon: Building2,
    iconColor: "text-indigo-600",
    iconBgColor: "bg-indigo-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.UNIVERSITY_DETAILS,
    variantsConfig: UNIVERSITY_VARIANTS_CONFIG,
    api: {
      getActive: () => getUniversityById(UNIVERSITY_ID),
    },
    ui: {
      showCreateButton: false,
      isDirectForm: true,
      emptyMessage: "",
      sidebarTitle: "University Details",
    },
  },
  // DB-backed config tabs. Rendered by AppConfigForm (intercepted in the settings
  // page), so they need no variantsConfig / redux / Form_1 wiring.
  app_config_university: {
    key: "app_config_university",
    label: "University Settings",
    labelPlural: "University Settings",
    description: "Gupshup credentials, OTP template, application URL and llms.txt.",
    icon: KeyRound,
    iconColor: "text-rose-600",
    iconBgColor: "bg-rose-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.APP_CONFIG_UNIVERSITY,
    variantsConfig: {},
    api: {},
    ui: {
      showCreateButton: false,
      isDirectForm: true,
      emptyMessage: "",
      sidebarTitle: "University Settings",
    },
  },
  app_config_app: {
    key: "app_config_app",
    label: "App Settings",
    labelPlural: "App Settings",
    description: "Chatbot ids and analytics/marketing tags (GTM, Ads, Pixel, UET…).",
    icon: Megaphone,
    iconColor: "text-amber-600",
    iconBgColor: "bg-amber-100",
    sectionType: UNIVERSITY_SETTINGS_SECTION_TYPES.APP_CONFIG_APP,
    variantsConfig: {},
    api: {},
    ui: {
      showCreateButton: false,
      isDirectForm: true,
      emptyMessage: "",
      sidebarTitle: "App Settings",
    },
  },
};

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

export const getUniversitySettingConfig = (key: UniversitySettingKey): UniversitySettingConfig => {
  return UNIVERSITY_SETTINGS_CONFIG[key];
};

export const getAllUniversitySettingConfigs = (): UniversitySettingConfig[] => {
  return Object.values(UNIVERSITY_SETTINGS_CONFIG);
};

export const getVariantDetails = (typeKey: UniversitySettingKey, variantId: string): VariantConfig | undefined => {
  const typeConfig = UNIVERSITY_SETTINGS_CONFIG[typeKey];
  return typeConfig?.variantsConfig[variantId];
};

